import { LazyQueryResult } from '@apollo/client';
import { defaultValues as blockDefaultValues, BlockDrawerValuesProps } from '@bechtel/client-blocks';
import { CacheKeys, useAppUser, useConfig, useModulePermission, useSessionStorage } from '@bechtel/client-common';
import {
  Carousel,
  ShiftLog,
  StatBlockGrid,
  StatBlockRow,
  useDashboardPermissions,
} from '@bechtel/client-dashboard-common';
import { drawerValues as defaultValues, DrawerValuesProps } from '@bechtel/client-work-packages';
import { aboveBreakpoint, Box, Theme, useMediaQuery } from '@bechtel/gds';
import {
  CrewMember,
  MilestoneFilterStatusFlag,
  OwnershipType,
  WorkPackageFilterByTerm,
  WorkPackageStats,
  WorkPackageStatsInput,
} from '@bechtel/types';
import { t } from '@lingui/macro';
import { get } from 'lodash';
import { FC, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { dashBoardStatsMock } from '~/form-data';
import { StatBlockStyled } from '~/styled';
import { getStatBlockGridData, isWorkPackagePersona } from '~/utilities';
import { WeeklyWorklist } from '~/worklist';
import { WorkpackageList } from '~/workpackage-list';
import { CommittedMilestoneStatsMock } from './form-data';
import {
  DashBoardLeftSectionStyled,
  SectionListStyled,
  SupDashboardContentStyled,
  SupDashboardStatStyled,
  WeeklyWorkListContainerStyled,
  WorkPackageContainerStyled,
} from './styled';

interface SuperIntendentDashboardContentProps {
  crewMember: CrewMember | null | undefined;
  selectedOwnershipType: OwnershipType;
  result: LazyQueryResult<{ workPackageStats: WorkPackageStats }, { input: WorkPackageStatsInput }>;
}

export const SuperIntendentDashboardContent: FC<SuperIntendentDashboardContentProps> = ({
  crewMember,
  selectedOwnershipType,
  result,
}) => {
  const { canViewBlockModule, canViewWorkPackageModule } = useModulePermission();
  const { canViewDownloadWeeklyWorkList } = useDashboardPermissions();
  const navigate = useNavigate();
  const { user } = useAppUser();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));
  const setDrawerValues = useSessionStorage<DrawerValuesProps>(CacheKeys.WorkPackageDrawerValues)[1];
  const setCursor = useSessionStorage<number>(CacheKeys.WorkPackageListCursor)[1];
  const setBlockDrawerValues = useSessionStorage<BlockDrawerValuesProps>(CacheKeys.BlockDrawerValues)[1];
  const { called, loading, data } = result;
  const { features } = useConfig();
  const superintendentDashboardStats =
    crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)
      ? get(data, 'workPackageStats', dashBoardStatsMock)
      : dashBoardStatsMock;
  const {
    crew,
    numWorkPackages,
    numBlocks,
    numRejects,
    plannedMilestones,
    earnedMilestones,
    approvedMilestones,
    committedMilestones,
  } = superintendentDashboardStats;

  const plannedMilestoneStats = getStatBlockGridData(
    plannedMilestones,
    committedMilestones ? committedMilestones : CommittedMilestoneStatsMock,
    user
  );
  const claimedMilestoneStats = getStatBlockGridData(earnedMilestones, undefined, user);
  const ApprovedMilestoneStats = getStatBlockGridData(approvedMilestones, undefined, user);

  const navigateToWorkPackage = async (values: DrawerValuesProps) => {
    if (user && user.crewMember && !isWorkPackagePersona(user.crewMember.persona)) {
      //TODO: Enable filter by owners in drawer
      // const filterByOwners = {
      //   persona: crewMember.persona,
      //   crewMemberId: crewMember.id,
      // };
      values = { ...values, searchScope: false };
    }
    setCursor(1);
    await setDrawerValues(values);
    navigate('work-packages');
  };
  const statsLoading =
    (!called && crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)) || loading;
  return (
    <SupDashboardContentStyled>
      <SupDashboardStatStyled>
        <StatBlockRow
          value={crew && crew.numCrews ? crew.numCrews.toString() : '0'}
          valueText={t`Crews`}
          total={crew && crew.numCrewMembers ? crew.numCrewMembers.toString() : '0'}
          icon="user-hard-hat"
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numWorkPackages)}
          label={t`Work Packages`}
          icon="briefcase"
          style={{ cursor: canViewWorkPackageModule && numWorkPackages > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewWorkPackageModule && numWorkPackages > 0) {
              navigateToWorkPackage({ ...defaultValues, filterBy: WorkPackageFilterByTerm.ALL });
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numBlocks)}
          label={t`Blocks`}
          icon="exclamation-triangle"
          style={{ cursor: canViewBlockModule && numBlocks > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewBlockModule && numBlocks > 0) {
              await setBlockDrawerValues(blockDefaultValues);
              navigate('blocks');
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numRejects)}
          label={t`Rejections`}
          icon="comment-alt-times"
          onClick={() => {
            navigateToWorkPackage({ ...defaultValues, filterBy: MilestoneFilterStatusFlag.REJECTED });
          }}
          loading={statsLoading}
        />
        <StatBlockGrid data={plannedMilestoneStats} label={t`Planned`} subLabel={t`Committed`} loading={statsLoading} />
        <StatBlockGrid data={claimedMilestoneStats} label={t`Claimed`} loading={statsLoading} />
        <StatBlockGrid data={ApprovedMilestoneStats} label={t`Approved`} loading={statsLoading} />
      </SupDashboardStatStyled>
      {isWideScreen ? (
        <SectionListStyled>
          <WorkPackageContainerStyled>
            <DashBoardLeftSectionStyled>
              <WorkpackageList crewMember={crewMember} selectedOwnershipType={selectedOwnershipType} />
              {features?.showShiftLog && (
                <Box mt={{ sm: '500' }}>
                  <ShiftLog />
                </Box>
              )}
            </DashBoardLeftSectionStyled>
          </WorkPackageContainerStyled>
          <WeeklyWorkListContainerStyled>
            <WeeklyWorklist showDownload={canViewDownloadWeeklyWorkList} showOwners={true} crewMember={crewMember} />
          </WeeklyWorkListContainerStyled>
        </SectionListStyled>
      ) : (
        <Carousel>
          <WorkpackageList crewMember={crewMember} selectedOwnershipType={selectedOwnershipType} />
          <WeeklyWorklist showDownload={canViewDownloadWeeklyWorkList} showOwners={true} crewMember={crewMember} />
          {features?.showShiftLog && <ShiftLog />}
        </Carousel>
      )}
    </SupDashboardContentStyled>
  );
};
